import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { giftCardsAPI } from '@/lib/api';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, Loader2, Check, Zap } from 'lucide-react';

const AMOUNTS = [25, 50, 75, 100, 150, 200];

export default function GiftCardsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const finalAmount = customAmount ? parseFloat(customAmount) : selectedAmount;

  const handlePurchase = async () => {
    if (!user) { toast.error('Connectez-vous pour acheter une carte cadeau'); navigate('/auth'); return; }
    if (finalAmount < 10 || finalAmount > 500) { toast.error('Montant entre 10€ et 500€'); return; }
    setSubmitting(true);
    try {
      const res = await giftCardsAPI.purchase({
        amount: finalAmount, recipient_name: recipientName,
        recipient_email: recipientEmail, message, origin_url: window.location.origin
      });
      window.location.href = res.data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="gift-cards-page">
      {/* Header */}
      <div className="relative overflow-hidden bg-[#0A0A0A] py-20 border-b border-[#1A1A1A]">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-[#00FF00]/8 to-transparent rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-xl">
            <p className="font-accent text-sm uppercase tracking-widest text-[#00FF00] font-bold mb-3">Cartes cadeaux</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="gift-cards-title">
              Offrez des sensations
            </h1>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed">
              La carte cadeau Riding Watt est valable sur toutes nos activités et dans tous nos centres.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left */}
          <div className="lg:col-span-3 space-y-8">
            <div>
              <h3 className="font-heading text-lg font-bold text-white mb-4">Choisissez le montant</h3>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {AMOUNTS.map((amt) => (
                  <button key={amt} onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                    className={`py-4 rounded-xl font-heading text-lg font-bold transition-all duration-200 ${
                      selectedAmount === amt && !customAmount
                        ? 'bg-[#00FF00] text-black shadow-[0_0_20px_rgba(0,255,0,0.3)]'
                        : 'bg-[#1A1A1A] border border-[#2A2A2A] text-slate-300 hover:border-[#00FF00]'
                    }`}
                    data-testid={`amount-${amt}`}>
                    {amt}€
                  </button>
                ))}
              </div>
              <div>
                <Label className="text-sm text-slate-500 mb-2">Ou montant personnalisé (10€ - 500€)</Label>
                <Input type="number" min="10" max="500" value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)} placeholder="Ex: 120"
                  className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                  data-testid="custom-amount-input" />
              </div>
            </div>

            <div>
              <h3 className="font-heading text-lg font-bold text-white mb-4">Destinataire (optionnel)</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-300 mb-1.5">Nom</Label>
                  <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="Jean Dupont"
                    className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                    data-testid="recipient-name" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-300 mb-1.5">Email</Label>
                  <Input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="jean@email.com"
                    className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                    data-testid="recipient-email" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-300 mb-1.5">Message</Label>
                  <Input value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder="Joyeux anniversaire !"
                    className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                    data-testid="gift-message" />
                </div>
              </div>
            </div>
          </div>

          {/* Right */}
          <div className="lg:col-span-2">
            <div className="sticky top-24 space-y-6">
              <div className="w-full aspect-[1.6/1] rounded-2xl bg-[#00FF00] shadow-[0_0_40px_rgba(0,255,0,0.2)] flex flex-col justify-between p-6 transform hover:-rotate-1 transition-transform duration-500">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-black" />
                    <span className="font-heading text-sm font-bold text-black">RIDINGWATT</span>
                  </div>
                  <span className="font-heading text-xs font-bold text-black/50 uppercase">Carte Cadeau</span>
                </div>
                {recipientName && <p className="text-sm text-black/70">Pour : {recipientName}</p>}
                <div>
                  <p className="text-xs text-black/50 mb-1">Valeur</p>
                  <p className="font-heading text-4xl font-bold text-black" data-testid="gift-card-preview-amount">{finalAmount}€</p>
                </div>
              </div>

              <div className="glass-card rounded-2xl p-6 space-y-3">
                {['Valable sur toutes les activités', 'Utilisable à Hyères et Toulouse', 'Validité 1 an', 'Envoi instantané par email'].map((feat) => (
                  <div key={feat} className="flex items-center gap-2 text-sm text-slate-400">
                    <Check className="w-4 h-4 text-[#00FF00] flex-shrink-0" /> {feat}
                  </div>
                ))}
              </div>

              <Button onClick={handlePurchase} disabled={submitting}
                className="w-full btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_30px_rgba(0,255,0,0.3)] text-base py-6 font-bold"
                data-testid="purchase-gift-card-btn">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirection...</> :
                  <>Acheter {finalAmount}€ <ArrowRight className="ml-2 w-5 h-5" /></>}
              </Button>
              <p className="text-xs text-slate-600 text-center">Paiement sécurisé par Revolut</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
